// anteilsbeziehung.ts — Anteilsbeziehungen richtungsunabhängig lesen.
//
// Gegenstück zu apps/api/src/anteilsbeziehung.ts (die Seiten teilen keinen
// Code; die Regel steht dort ausführlich). Kurz:
//
//   'Gesellschafter'  src = Gesellschaft, dest = Gesellschafter
//   'Beteiligung'     src = Gesellschaft, dest = Gesellschafter   (T2, 08.09.2026)
//   'Tochterfirma'    src = Mutter (Halter), dest = Tochter
//   'Mutterfirma'     src = Tochter, dest = Mutter                 (Altlabel)
//
// Dieselbe Beteiligung steht deshalb oft ZWEIMAL in den Daten — als
// 'Tochterfirma' der Mutter und als 'Gesellschafter' der Tochter
// (ensureGesellschafterAusMutter). In der Akte und im Organigramm ist sie EINE
// Verbindung (Meldung „zweimal quip", 28.09.2026).
//
// Bewusst ohne React, damit die Regel per Vitest prüfbar ist.

export type AnteilsKlasse = "struktur" | "anteil";

export type AnteilsFakt = {
  gesellschaftId: number;
  halterId: number;
  klasse: AnteilsKlasse;
};

const SRC_IST_GESELLSCHAFT: Record<string, AnteilsKlasse> = {
  Gesellschafter: "anteil",
  Beteiligung: "anteil",
  Mutterfirma: "struktur",
};
const SRC_IST_HALTER: Record<string, AnteilsKlasse> = {
  Tochterfirma: "struktur",
};

/** Gespeicherte Zeile → Tatsache „halterId hält Anteile an gesellschaftId", sonst `null`. */
export function anteilsFakt(
  relationType: string | null | undefined,
  srcId: number | null | undefined,
  destId: number | null | undefined,
): AnteilsFakt | null {
  const typ = (relationType ?? "").trim();
  if (typeof srcId !== "number" || typeof destId !== "number" || srcId === destId) return null;
  const k1 = SRC_IST_GESELLSCHAFT[typ];
  if (k1) return { gesellschaftId: srcId, halterId: destId, klasse: k1 };
  const k2 = SRC_IST_HALTER[typ];
  if (k2) return { gesellschaftId: destId, halterId: srcId, klasse: k2 };
  return null;
}

export const faktSchluessel = (f: Pick<AnteilsFakt, "gesellschaftId" | "halterId">): string =>
  `${f.gesellschaftId}>${f.halterId}`;

/** Beschreiben zwei Zeilen dieselbe Beteiligung (egal in welcher Form)? */
export const gleicheBeteiligung = (a: AnteilsFakt | null, b: AnteilsFakt | null): boolean =>
  !!a && !!b && faktSchluessel(a) === faktSchluessel(b);

const leer = (s?: string | null): boolean => !(s ?? "").trim();

/** Quoten vergleichbar machen: „55,14 %" = „55,14%" = „55.14 %". */
export const anteilNormal = (s?: string | null): string =>
  (s ?? "").replace(/\s+/g, "").replace(",", ".").replace(/%$/, "").toLowerCase();

/** Die verschiedenen, nicht leeren Quoten — mehr als eine heißt: Widerspruch. */
export function verschiedeneAnteile(anteile: Array<string | null | undefined>): string[] {
  const out: string[] = [];
  const gesehen = new Set<string>();
  for (const a of anteile) {
    if (leer(a)) continue;
    const key = anteilNormal(a);
    if (gesehen.has(key)) continue;
    gesehen.add(key);
    out.push((a ?? "").trim());
  }
  return out;
}

/** Anzeige einer Quote mit Widerspruch: „60 % / 100 % (widersprüchlich)". */
export function anteilAnzeige(share?: string | null, widerspruch?: string[] | null): string {
  if (widerspruch && widerspruch.length > 1) return `${widerspruch.join(" / ")} (widersprüchlich)`;
  return (share ?? "").trim();
}

/**
 * Anteile eines Eintrags aus der Maske: Tragen Leit- und Spiegelzeile
 * verschiedene Quoten, steht das da — solange niemand die Quote geändert hat
 * (dann gilt die neue, und der Abgleich zieht sie an alle Zeilen).
 */
export function eintragAnteile(it: { share?: string | null; anteilWiderspruch?: string[] | null }): string {
  const sh = (it.share ?? "").trim();
  const w = it.anteilWiderspruch;
  return w && w.length > 1 && w[0] === sh ? anteilAnzeige(sh, w) : sh;
}

/**
 * Rang der Leitzeile einer Beteiligung: Die Struktur-Zeile gewinnt (sie trägt
 * den Verbund und den Stift in „Verbindungen zu Firmen"), dann Beteiligung,
 * dann Gesellschafter.
 */
export const LEIT_RANG: Readonly<Record<string, number>> = {
  Tochterfirma: 0,
  Mutterfirma: 1,
  Beteiligung: 2,
  Gesellschafter: 3,
};

/** Eine Zeile, die der Deduplizierung genügt. */
export type AnteilsZeile = {
  /** Gespeicherter Typ. */
  typ: string;
  srcId?: number | null;
  destId?: number | null;
  share?: string | null;
};

/**
 * Zeilen, die dieselbe Beteiligung beschreiben, zu EINER zusammenfassen.
 *
 * Liefert je Gruppe die Leitzeile — mit der ersten vorhandenen Quote, den
 * übrigen Zeilen als `spiegel` und `anteilWiderspruch`, falls die Quoten
 * auseinanderlaufen. Zeilen ohne Anteilsbeziehung bleiben unverändert und in
 * ihrer Reihenfolge.
 */
export function fasseAnteilsZeilenZusammen<T>(
  zeilen: T[],
  lies: (z: T) => AnteilsZeile,
): Array<{ zeile: T; spiegel: T[]; share?: string; anteilWiderspruch?: string[] }> {
  const gruppen = new Map<string, T[]>();
  for (const z of zeilen) {
    const r = lies(z);
    const f = anteilsFakt(r.typ, r.srcId, r.destId);
    if (!f) continue;
    const key = faktSchluessel(f);
    gruppen.set(key, [...(gruppen.get(key) ?? []), z]);
  }
  const leitVon = new Map<T, { spiegel: T[]; share?: string; anteilWiderspruch?: string[] }>();
  const weg = new Set<T>();
  for (const liste of gruppen.values()) {
    if (liste.length < 2) continue;
    const sortiert = [...liste].sort(
      (a, b) =>
        (LEIT_RANG[lies(a).typ] ?? 9) - (LEIT_RANG[lies(b).typ] ?? 9) ||
        Number(leer(lies(a).share)) - Number(leer(lies(b).share)),
    );
    const [leit, ...rest] = sortiert;
    const anteile = verschiedeneAnteile(sortiert.map((z) => lies(z).share));
    leitVon.set(leit, {
      spiegel: rest,
      ...(anteile[0] ? { share: anteile[0] } : {}),
      ...(anteile.length > 1 ? { anteilWiderspruch: anteile } : {}),
    });
    rest.forEach((z) => weg.add(z));
  }
  const out: Array<{ zeile: T; spiegel: T[]; share?: string; anteilWiderspruch?: string[] }> = [];
  for (const z of zeilen) {
    if (weg.has(z)) continue;
    const l = leitVon.get(z);
    if (l) out.push({ zeile: z, ...l });
    else {
      const sh = lies(z).share;
      out.push({ zeile: z, spiegel: [], ...(leer(sh) ? {} : { share: (sh ?? "").trim() }) });
    }
  }
  return out;
}

/**
 * Anzeigezeilen der Akte (beide Richtungen schon aufgelöst) ohne die zweite
 * Zeile derselben Beteiligung — nur zwischen Firmen. Die Leitzeile übernimmt
 * die Quote, falls sie selbst keine trägt.
 *
 * Personen bleiben unberührt: Bei ihnen zeigt die Akte Rollen (Geschäftsführer,
 * Gesellschafter …) je Person gebündelt, und Altdaten sind dort teils
 * Person→Firma gespeichert — die Richtung wäre nicht verlässlich.
 */
export function ohneAnteilsDubletten<
  T extends { storedRelType?: string; relType: string; srcId?: number; destId?: number; share?: string; otherType: string },
>(zeilen: T[]): T[] {
  const firmen = zeilen.filter((z) => z.otherType === "Firma");
  const gebuendelt = fasseAnteilsZeilenZusammen(firmen, (z) => ({
    typ: z.storedRelType ?? z.relType,
    srcId: z.srcId,
    destId: z.destId,
    share: z.share,
  }));
  const bleibt = new Map<T, T>();
  for (const g of gebuendelt) {
    bleibt.set(g.zeile, g.spiegel.length && g.share && !g.zeile.share ? { ...g.zeile, share: g.share } : g.zeile);
  }
  return zeilen.flatMap((z) => (z.otherType !== "Firma" ? [z] : bleibt.has(z) ? [bleibt.get(z)!] : []));
}
